import React, { useState } from 'react';
import { passkeyBeschikbaar } from '../services/passkey';
import { useNavigate } from 'react-router-dom';
import { isFormsOnly } from '../utils/host';

import { useAuth } from '../context/AuthContext';
import './Login.css';

const Login = () => {
  const [gebruikersnaam, setGebruikersnaam] = useState('');
  const [wachtwoord, setWachtwoord] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // 2FA / MFA state
  const [mfaMode, setMfaMode] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [setupMode, setSetupMode] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [setupToken, setSetupToken] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [setupGebruiker, setSetupGebruiker] = useState(null);
  const { login, loginMetPasskey, verifyMfaLogin, forcedSetupStart, forcedSetupComplete } = useAuth();
  const navigate = useNavigate();

  const passkeyInloggen = async () => {
    setLoading(true); setError('');
    try { const r = await loginMetPasskey(gebruikersnaam || undefined); if (!r.success) setError(r.message); else redirectAfterLogin(r.gebruiker); }
    catch (e) { setError(e?.name === 'NotAllowedError' ? 'Passkey geannuleerd.' : (e?.response?.data?.message || 'Passkey-login mislukt')); }
    finally { setLoading(false); }
  };
  const redirectAfterLogin = (gebruiker) => {
    if (isFormsOnly()) {
      navigate('/forms/inbox');
    } else if (gebruiker?.rol === 'super_admin') {
      navigate('/admin');
    } else {
      navigate('/dashboard');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(gebruikersnaam, wachtwoord);

    if (result.requiresMfa) {
      setMfaToken(result.mfaToken);
      setMfaMode(true);
      setLoading(false);
      setMfaCode('');
      return;
    }

    if (result.requiresMfaSetup) {
      const s = await forcedSetupStart(result.setupToken);
      if (s.success) {
        setSetupToken(s.setup_token);
        setQrCode(s.qr_code);
        setSecret(s.secret);
        setSetupMode(true);
        setMfaCode('');
        setLoading(false);
      } else {
        setError(s.message);
        setLoading(false);
      }
      return;
    }

    if (result.success) {
      redirectAfterLogin(result.gebruiker);
    } else {
      setError(result.message);
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await verifyMfaLogin(mfaToken, mfaCode);

    if (result.success) {
      redirectAfterLogin(result.gebruiker);
    } else {
      setError(result.message);
      setLoading(false);
    }
  };

  const cancelMfa = () => {
    setMfaMode(false);
    setMfaToken('');
    setMfaCode('');
    setError('');
    setUseRecovery(false);
    setWachtwoord('');
  };

  const handleSetupSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await forcedSetupComplete(setupToken, mfaCode);
    if (result.success) {
      setSetupGebruiker(result.gebruiker);
      setRecoveryCodes(result.recoveryCodes || []);
      setRecoveryMode(true);
      setSetupMode(false);
      setLoading(false);
    } else {
      setError(result.message);
      setLoading(false);
    }
  };

  const finishRecovery = () => {
    const g = setupGebruiker;
    const target = isFormsOnly() ? '/forms/inbox' : (g && g.rol === 'super_admin' ? '/admin' : '/dashboard');
    window.location.href = target;
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1>WOM<span style={{color:"#00D4AA"}}>sys</span></h1>
          <p>{recoveryMode ? 'Bewaar je herstelcodes' : setupMode ? '2FA instellen (verplicht)' : mfaMode ? 'Tweestapsverificatie' : 'Log in om door te gaan'}</p>
        </div>

        {recoveryMode ? (
            <div className="login-form">
              <p style={{color:"#94a3b8", fontSize:"14px", textAlign:"center", marginBottom:"16px"}}>
                2FA is geactiveerd. Bewaar deze herstelcodes veilig - bruikbaar als je je telefoon kwijt bent. Ze worden maar een keer getoond.
              </p>
              <div style={{ background:"rgba(255,255,255,0.05)", border:"1px solid rgba(0,212,170,0.3)", borderRadius:"10px", padding:"14px", marginBottom:"16px", textAlign:"center" }}>
                {recoveryCodes.map((rc, i) => (
                  <div key={i} style={{ fontFamily:"monospace", fontSize:"15px", color:"#fff", padding:"3px 0", letterSpacing:"1px" }}>{rc}</div>
                ))}
              </div>
              <button type="button" className="btn-primary" onClick={finishRecovery}>
                Ik heb ze opgeslagen - doorgaan
              </button>
            </div>
          ) : setupMode ? (
            <form onSubmit={handleSetupSubmit} className="login-form">
              {error && (<div className="error-message">{error}</div>)}
              <p style={{color:"#94a3b8", fontSize:"14px", textAlign:"center", marginBottom:"16px"}}>
                Je organisatie vereist 2FA. Scan de QR-code met Google Authenticator, Microsoft Authenticator of Authy.
              </p>
              {qrCode && (
                <div style={{ textAlign:"center", marginBottom:"12px" }}>
                  <img src={qrCode} alt="QR-code" style={{ width:200, height:200, borderRadius:"8px", background:"#fff", padding:"8px" }} />
                </div>
              )}
              {secret && (
                <div style={{ background:"rgba(255,255,255,0.05)", border:"1px solid rgba(0,212,170,0.3)", borderRadius:"10px", padding:"10px", marginBottom:"16px", textAlign:"center" }}>
                  <div style={{ fontSize:"11px", color:"#94a3b8", marginBottom:"4px" }}>Of voer deze sleutel handmatig in:</div>
                  <code style={{ fontSize:"13px", color:"#00D4AA", wordBreak:"break-all" }}>{secret}</code>
                </div>
              )}
              <div className="form-group">
                <label htmlFor="setupCode" style={{color:"#00D4AA"}}>Code uit de app</label>
                <input
                  type="text"
                  id="setupCode"
                  style={{ background:"rgba(255,255,255,0.05)", color:"#fff", border:"1.5px solid rgba(0,212,170,0.3)", borderRadius:"10px", fontSize:"24px", letterSpacing:"8px", textAlign:"center", fontFamily:"monospace" }}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="123456"
                  maxLength={6}
                  autoFocus
                  inputMode="numeric"
                  disabled={loading}
                  required
                />
              </div>
              <button type="submit" className="btn-primary" disabled={loading || mfaCode.length < 6}>
                {loading ? 'Activeren...' : '2FA activeren'}
              </button>
            </form>
          ) : mfaMode ? (
          <form onSubmit={handleMfaSubmit} className="login-form">
            {error && (<div className="error-message">{error}</div>)}

            <p style={{color:"#94a3b8", fontSize:"14px", textAlign:"center", marginBottom:"16px"}}>
              {useRecovery
                ? 'Vul een recovery code in (xxxx-xxxx)'
                : 'Vul de 6-cijferige code uit je Authenticator-app in'}
            </p>

            <div className="form-group">
              <label htmlFor="mfaCode" style={{color:"#00D4AA"}}>
                {useRecovery ? 'Recovery code' : 'Code'}
              </label>
              <input
                type="text"
                id="mfaCode"
                style={{
                  background:"rgba(255,255,255,0.05)",
                  color:"#fff",
                  border:"1.5px solid rgba(0,212,170,0.3)",
                  borderRadius:"10px",
                  fontSize: useRecovery ? "16px" : "24px",
                  letterSpacing: useRecovery ? "1px" : "8px",
                  textAlign:"center",
                  fontFamily: 'monospace'
                }}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.trim())}
                placeholder={useRecovery ? 'xxxx-xxxx' : '123456'}
                maxLength={useRecovery ? 9 : 6}
                autoFocus
                autoComplete="one-time-code"
                inputMode={useRecovery ? "text" : "numeric"}
                disabled={loading}
                required
              />
            </div>

            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <a
                onClick={() => { setUseRecovery(!useRecovery); setMfaCode(''); setError(''); }}
                style={{ color: '#00D4AA', cursor: 'pointer', fontSize: '13px', textDecoration: 'none' }}
              >
                {useRecovery
                  ? 'Terug naar Authenticator-code'
                  : 'Geen toegang tot je telefoon? Gebruik recovery code'}
              </a>
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={loading || mfaCode.length < (useRecovery ? 9 : 6)}
            >
              {loading ? 'Bezig met verifiëren...' : 'Verifiëren'}
            </button>

            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <a
                onClick={cancelMfa}
                style={{ color: '#94a3b8', cursor: 'pointer', fontSize: '13px', textDecoration: 'none' }}
              >
                ← Terug naar inloggen
              </a>
            </div>
          </form>
        ) : (
          <>
          {passkeyBeschikbaar() && (
            <button type="button" onClick={passkeyInloggen} disabled={loading} className="btn-secondary" style={{ width: '100%', marginBottom: 12 }}>🔑 Inloggen met passkey</button>
          )}
          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="gebruikersnaam" style={{color:"#00D4AA"}}>Gebruikersnaam</label>
              <input
                type="text"
                id="gebruikersnaam"
                style={{background:"rgba(255,255,255,0.05)",color:"#fff",border:"1.5px solid rgba(0,212,170,0.3)",borderRadius:"10px"}}
                value={gebruikersnaam}
                onChange={(e) => setGebruikersnaam(e.target.value)}
                required
                autoFocus
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="wachtwoord" style={{color:"#00D4AA"}}>Wachtwoord</label>
              <input
                type="password"
                id="wachtwoord"
                style={{background:"rgba(255,255,255,0.05)",color:"#fff",border:"1.5px solid rgba(0,212,170,0.3)",borderRadius:"10px"}}
                value={wachtwoord}
                onChange={(e) => setWachtwoord(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div style={{ textAlign: 'right', marginBottom: '16px' }}>
              <a
                onClick={() => navigate('/wachtwoord-vergeten')}
                style={{
                  color: '#00D4AA',
                  cursor: 'pointer',
                  fontSize: '14px',
                  textDecoration: 'none'
                }}
              >
                Wachtwoord vergeten?
              </a>
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Bezig met inloggen...' : 'Inloggen'}
            </button>
          </form>
          </>
        )}

        <div className="login-footer">
        </div>
      </div>
    </div>
  );
};

export default Login;
